import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Coffee, Heart, Loader2, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import { useT } from "@/i18n/LanguageContext";
import GooglePayButton from "@/components/GooglePayButton";
import TipThanks from "@/components/TipThanks";

const MIN_CUSTOM = 1;
const MAX_CUSTOM = 500;
const CUSTOM_KEY = "__custom__";
// Hardcoded tier fallback so the dialog never flashes an empty / grayed-out
// state if `/api/tips/tiers` is slow or temporarily unreachable. Amounts
// are in pence to match the backend.
const FALLBACK_TIERS = [
  { lookup_key: "tip_small",     label: "Small coffee",  amount: 300,  emoji: "☕" },
  { lookup_key: "tip_medium",    label: "Medium coffee", amount: 500,  emoji: "☕" },
  { lookup_key: "tip_large",     label: "Big thanks",    amount: 1000, emoji: "🙌" },
  { lookup_key: "tip_supporter", label: "Supporter",     amount: 2500, emoji: "💝" },
  { lookup_key: "tip_patron",    label: "Patron",        amount: 5000, emoji: "🌟" },
];

export default function TipJarDialog({ open, onOpenChange }) {
  const { t } = useT();
  const [tiers, setTiers] = useState(FALLBACK_TIERS);
  const [selected, setSelected] = useState("tip_medium");
  const [customAmount, setCustomAmount] = useState("");
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [thanks, setThanks] = useState(null);

  useEffect(() => {
    if (!open) return;
    api.get("/tips/tiers").then((r) => {
      if (Array.isArray(r.data) && r.data.length) setTiers(r.data);
    }).catch(() => {});
  }, [open]);

  const isCustom = selected === CUSTOM_KEY;
  const customValue = parseFloat(customAmount);
  const customInvalid = isCustom && (isNaN(customValue) || customValue < MIN_CUSTOM || customValue > MAX_CUSTOM);

  const checkout = async (provider = "stripe") => {
    if (!selected) return;
    if (isCustom && customInvalid) {
      toast.error(t("tip.invalidAmount"));
      return;
    }
    try {
      setLoading(provider);
      const payload = {
        origin_url: window.location.origin,
        name: name.trim(),
        message: message.trim(),
      };
      if (isCustom) payload.custom_amount_cents = Math.round(customValue * 100);
      else payload.lookup_key = selected;

      if (provider === "paypal") {
        const { data } = await api.post("/paypal/create-order", payload);
        // Open PayPal in a new tab so the Mango Notes page keeps the user's
        // draft/notes state intact if they close the checkout tab.
        window.open(data.approve_url, "_blank", "noopener,noreferrer");
        setLoading(false);
        return;
      }
      const { data } = await api.post("/payments/checkout", payload);
      window.open(data.checkout_url, "_blank", "noopener,noreferrer");
      setLoading(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail ? `${t("tip.couldNotStart")} — ${detail}` : t("tip.couldNotStart"));
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-0 overflow-hidden" data-testid="tip-jar-dialog">
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="absolute top-3 right-3 z-20 h-9 w-9 inline-flex items-center justify-center rounded-full bg-white/80 dark:bg-stone-900/80 backdrop-blur hover:bg-white dark:hover:bg-stone-900 shadow-sm"
          data-testid="tip-close-btn"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
        {thanks && (
          <div className="absolute inset-0 z-30 bg-background flex items-center justify-center p-6" data-testid="tip-thanks-overlay">
            <TipThanks
              amountCents={thanks.amount}
              message={thanks.message}
              onDone={() => { setThanks(null); onOpenChange(false); }}
            />
          </div>
        )}
        <div className="bg-gradient-to-br from-amber-100 via-rose-100 to-amber-50 dark:from-amber-900/40 dark:via-rose-900/30 dark:to-amber-950/40 px-6 pt-6 pb-5">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Coffee className="h-6 w-6 text-amber-700 dark:text-amber-300" />
              {t("tip.title")}
            </DialogTitle>
            <DialogDescription className="text-sm text-stone-700 dark:text-stone-300">
              {t("tip.subtitle")}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="tip-tier-grid">
            {tiers.map((tier) => {
              const active = tier.lookup_key === selected;
              return (
                <button
                  key={tier.lookup_key}
                  onClick={() => setSelected(tier.lookup_key)}
                  data-testid={`tip-tier-${tier.lookup_key}`}
                  className={cn(
                    "rounded-2xl border p-3 flex flex-col items-center gap-1 transition-all",
                    "hover:-translate-y-0.5 hover:shadow-md",
                    active
                      ? "border-stone-900 dark:border-stone-100 bg-amber-50 dark:bg-amber-950/40 shadow-sm"
                      : "border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900"
                  )}
                >
                  <span className="text-xl leading-none">{tier.emoji}</span>
                  <span className="font-display font-semibold text-sm">£{(tier.amount / 100).toFixed(0)}</span>
                  <span className="text-[10px] text-stone-500 dark:text-stone-400 text-center leading-tight">
                    {tier.label}
                  </span>
                </button>
              );
            })}
            <button
              onClick={() => setSelected(CUSTOM_KEY)}
              data-testid="tip-tier-custom"
              className={cn(
                "rounded-2xl border p-3 flex flex-col items-center gap-1 transition-all",
                "hover:-translate-y-0.5 hover:shadow-md",
                isCustom
                  ? "border-stone-900 dark:border-stone-100 bg-amber-50 dark:bg-amber-950/40 shadow-sm"
                  : "border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900"
              )}
            >
              <Sparkles className="h-5 w-5 text-amber-600" />
              <span className="font-display font-semibold text-sm">£?</span>
              <span className="text-[10px] text-stone-500 dark:text-stone-400 text-center leading-tight">
                {t("tip.custom")}
              </span>
            </button>
          </div>

          {isCustom && (
            <div className="rounded-2xl border border-amber-300 dark:border-amber-700/60 bg-amber-50/70 dark:bg-amber-950/30 p-4">
              <label className="font-mono-xs text-stone-500 dark:text-stone-400 mb-1 block">
                {t("tip.customAmountLabel")}
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500">£</span>
                <Input
                  type="number"
                  inputMode="decimal"
                  min={MIN_CUSTOM}
                  max={MAX_CUSTOM}
                  step="0.5"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  placeholder="5.00"
                  className={cn("pl-7 h-11 text-lg font-display", customInvalid && customAmount && "border-rose-400")}
                  data-testid="tip-custom-amount-input"
                />
              </div>
              <p className="text-xs text-stone-500 dark:text-stone-400 mt-1.5">{t("tip.customHint")}</p>
            </div>
          )}

          <div className="space-y-3">
            <div>
              <label className="font-mono-xs text-stone-500 dark:text-stone-400 mb-1 block">{t("tip.yourName")}</label>
              <Input value={name} onChange={(e) => setName(e.target.value)}
                placeholder={t("tip.yourNamePh")} maxLength={120} data-testid="tip-name-input" />
            </div>
            <div>
              <label className="font-mono-xs text-stone-500 dark:text-stone-400 mb-1 block">{t("tip.message")}</label>
              <Textarea value={message} onChange={(e) => setMessage(e.target.value)}
                placeholder={t("tip.messagePh")} maxLength={400} className="min-h-[72px]" data-testid="tip-message-input" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Button
              onClick={() => checkout("stripe")}
              disabled={!!loading || !selected || (isCustom && customInvalid)}
              className="w-full rounded-full h-11 text-sm sm:text-base font-semibold bg-stone-900 hover:bg-stone-800 dark:bg-stone-100 dark:text-stone-900 dark:hover:bg-stone-200 min-w-0"
              data-testid="tip-checkout-btn"
            >
              {loading === "stripe" ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin shrink-0" /><span className="truncate">{t("tip.opening")}</span></>)
                       : (<><Heart className="h-4 w-4 mr-1.5 shrink-0" /><span className="truncate text-xs sm:text-sm">Card · Apple Pay</span></>)}
            </Button>
            <Button
              onClick={() => checkout("paypal")}
              disabled={!!loading || !selected || (isCustom && customInvalid)}
              className="w-full rounded-full h-11 text-sm sm:text-base font-semibold bg-[#0070ba] hover:bg-[#005ea6] text-white dark:text-white"
              data-testid="tip-checkout-paypal-btn"
            >
              {loading === "paypal" ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin shrink-0" />Opening PayPal</>)
                       : (<span className="font-extrabold tracking-tight whitespace-nowrap"><span className="text-white">Pay</span><span className="text-sky-200">Pal</span></span>)}
            </Button>
          </div>

          {/* Google Pay — renders only after the browser confirms the user
              is logged into Google AND has a saved payment method. If the
              gate fails, the component renders nothing (no greyed button,
              no fallback text). */}
          <GooglePayButton
            lookupKey={isCustom ? null : selected}
            customAmountCents={isCustom && !customInvalid ? Math.round(customValue * 100) : null}
            amountCents={
              isCustom
                ? (customInvalid ? 0 : Math.round(customValue * 100))
                : (tiers.find((x) => x.lookup_key === selected)?.amount || 0)
            }
            name={name.trim()}
            message={message.trim()}
            onFallback={() => { if (!loading && selected && !(isCustom && customInvalid)) checkout("stripe"); }}
            onPaid={(res) => {
              setThanks({
                amount: res?.amount || (isCustom ? Math.round(customValue * 100) : (tiers.find((x) => x.lookup_key === selected)?.amount || 0)),
                message: message.trim(),
              });
            }}
          />

          <p className="text-xs text-stone-500 dark:text-stone-400 text-center -mt-1" data-testid="tip-wallets-hint">
            Google Pay appears above when your browser supports it. Apple Pay &amp; cards work from the Card button on every device.
          </p>

        </div>
      </DialogContent>
    </Dialog>
  );
}
